/**
 * PageBreadcrumb Component
 *
 * Reusable navigation component with back button, category image, and breadcrumb trail.
 *
 * FEATURES:
 * - Back button with customizable onClick handler
 * - Category/subcategory image display
 * - Breadcrumb navigation with clickable items
 * - Responsive layout
 * - Type-safe props with full TypeScript support
 *
 * USAGE:
 * <PageBreadcrumb
 *   backLabel="Categories"
 *   onBackClick={() => router.back()}
 *   categoryImage="/images/categories/cleaning.jpg"
 *   breadcrumbItems={[
 *     { label: 'Cleaning', onClick: handleCategoryClick },
 *     { label: 'Home Cleaning', isActive: true }
 *   ]}
 * />
 *
 * @example
 * // Simple breadcrumb with just back button
 * <PageBreadcrumb
 *   backLabel="Home"
 *   onBackClick={() => router.push('/')}
 * />
 *
 * @example
 * // Full breadcrumb with image and navigation
 * <PageBreadcrumb
 *   backLabel="All Categories"
 *   onBackClick={handleBack}
 *   categoryImage="/images/categories/repair.jpg"
 *   categoryImageAlt="Repair Services"
 *   breadcrumbItems={[
 *     { label: 'Home Services', onClick: () => router.push('/services') },
 *     { label: 'Repair', onClick: () => router.push('/services/repair') },
 *     { label: 'Appliance Repair', isActive: true }
 *   ]}
 * />
 */

'use client';

import { type HTMLAttributes } from 'react';
import { cn } from '@/src/shared/lib/utils';
import { Typography } from '@/src/shared/ui/typography/Typography';
import { Icon } from '@/src/shared/ui/icons/Icon';
import { Breadcrumb, type BreadcrumbItem } from './Breadcrumb';

export interface PageBreadcrumbProps extends Omit<HTMLAttributes<HTMLDivElement>, 'onClick'> {
  /**
   * Label for the back button (e.g., "Categories", "Home")
   */
  backLabel: string;

  /**
   * Click handler for the back button
   */
  onBackClick: () => void;

  /**
   * URL or path to the category image
   * If not provided, image section will not be rendered
   */
  categoryImage?: string;

  /**
   * Alt text for the category image
   * @default backLabel
   */
  categoryImageAlt?: string;

  /**
   * Array of breadcrumb items
   * If not provided, breadcrumb trail will not be rendered
   */
  breadcrumbItems?: BreadcrumbItem[];

  /**
   * Size of the category image in pixels
   * @default 64
   */
  imageSize?: number;

  /**
   * Typography variant for breadcrumb text
   * @default 'h2'
   */
  breadcrumbTextVariant?: 'h1' | 'h2' | 'h3' | 'body' | 'bodySm' | 'tag' | 'note';

  /**
   * Size of the breadcrumb separator icon
   * @default 'md'
   */
  breadcrumbIconSize?: 'xs' | 'sm' | 'md' | 'lg' | 'xl' | '2xl' | number;

  /**
   * Custom container classes
   */
  containerClassName?: string;

  /**
   * Custom back button classes
   */
  backButtonClassName?: string;

  /**
   * Custom breadcrumb classes
   */
  breadcrumbClassName?: string;
}

/**
 * PageBreadcrumb Component
 *
 * Displays a back button with breadcrumb navigation and optional category image.
 * Designed for consistent navigation across different pages.
 */
export const PageBreadcrumb: React.FC<PageBreadcrumbProps> = ({
  backLabel,
  onBackClick,
  categoryImage,
  categoryImageAlt,
  breadcrumbItems,
  imageSize = 200,
  breadcrumbTextVariant = 'h2',
  breadcrumbIconSize = 'md',
  containerClassName,
  backButtonClassName,
  breadcrumbClassName,
  className,
  ...props
}) => {
  return (
    <div
      className={cn('container-auto pt-6 pb-4', containerClassName, className)}
      {...props}
    >
      {/* Back Button */}
      <button
        onClick={onBackClick}
        className={cn(
          'flex items-center gap-2 text-text-primary hover:text-accent-primary transition-colors mb-3 lg:mb-4',
          backButtonClassName
        )}
        aria-label={`Back to ${backLabel}`}
      >
        <Icon name="arrow-left" size="xl" />
        <Typography variant="h1">
          {backLabel}
        </Typography>
      </button>

      {/* Category Image and Breadcrumb */}
      {(categoryImage || breadcrumbItems) && (
        <div className="flex items-center lg:items-end gap-3 lg:gap-4">
          {/* Category Image - responsive size handled by .category-image-circle
              (110px → 150px → 200px across breakpoints) */}
          {categoryImage && (
            <div className="category-image-circle">
              <img
                src={categoryImage}
                alt={categoryImageAlt || backLabel}
              />
            </div>
          )}

          {/* Breadcrumb Trail.
              On mobile it wraps and fills the remaining width so the title isn't
              squeezed next to the image (text shrink handled inside Breadcrumb). */}
          {breadcrumbItems && breadcrumbItems.length > 0 && (
            <Breadcrumb
              items={breadcrumbItems}
              textVariant={breadcrumbTextVariant}
              iconSize={breadcrumbIconSize}
              shrinkOnMobile
              className={cn('mb-4 lg:mb-10 min-w-0 flex-1 flex-wrap', breadcrumbClassName)}
            />
          )}
        </div>
      )}
    </div>
  );
};

PageBreadcrumb.displayName = 'PageBreadcrumb';
